import { twMerge } from 'tailwind-merge'
import { Link } from 'react-router-dom'
import { ClassModel } from '@models'
import classNames from 'classnames'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import utc from 'dayjs/plugin/utc.js'
import Button from '@/components/atoms/Button'
import Icon from '@/components/atoms/Icon'
import { Transition } from '@headlessui/react'

dayjs.extend(utc)
dayjs.locale('pt-br')

interface CardClassProps {
	liveClass: ClassModel.Show
	classIsHappening: boolean
	classInThePast: boolean
	isLoading?: boolean
	className?: string
	onClick?: () => void
	isActive?: boolean
}

export function CardClass({
	isActive,
	liveClass,
	classIsHappening,
	classInThePast,
	className,
	onClick,
	isLoading,
}: CardClassProps) {
	return (
		<Link
			to={classIsHappening ? liveClass.link_aula : '#'}
			className={twMerge(
				className,
				classNames(
					'w-full bg-zinc-900 border border-zinc-700 p-6 rounded-xl flex flex-col md:flex-row md:items-center justify-between cursor-default',
					{
						'!bg-brand-primary': isActive,
						'cursor-pointer': classIsHappening,
						'opacity-70': classInThePast,
						'animate-pulse pointer-events-none': isLoading,
					}
				)
			)}
			onClick={onClick}
		>
			<div className='flex flex-col gap-4'>
				<div className='flex flex-col gap-3'>
					<Transition show={classIsHappening}>
						<div className='transition duration-700 ease-in data-[closed]:opacity-0 px-2 bg-light-pink w-full max-w-max rounded-full'>
							<div className='flex items-center gap-1 animate-pulse'>
								<Icon name='live' width='24px' height='24px' />
								<p className='text-xs text-white font-bold uppercase pr-3'>
									Acontecendo agora
								</p>
							</div>
						</div>
					</Transition>
					<h3
						className={twMerge(
							'text-[22px] dark:text-white font-bold leading-[130%] capitalize',
							classNames({ 'text-white': isActive })
						)}
					>
						{liveClass.nome_aula}
					</h3>
					<p
						className={twMerge(
							'text-base text-[#312144]/70 font-medium dark:text-gray-200',
							classNames({ 'text-gray-300': isActive })
						)}
					>
						Data:{' '}
						<strong>
							{dayjs(liveClass.data_aula)
								.utc()
								.locale('pt-br')
								.format('dddd, DD/MM')
								.charAt(0)
								.toUpperCase() +
								dayjs(liveClass.data_aula)
									.utc()
									.locale('pt-br')
									.format('dddd, DD/MM')
									.slice(1)}{' '}
							às {liveClass.horario_inicio.slice(0, 5)}
						</strong>
					</p>
				</div>
				{(liveClass?.material || liveClass?.memhack) && (
					<div className='flex flex-col sm:flex-row gap-3'>
						{classIsHappening && (
							<Button
								className={twMerge(
									classNames(
										'w-full max-w-max h-auto min-h-[40px] text-xs xs:text-sm text-[#150E1D] bg-white hover:bg-white border border-gray-400/10 border-solid hover:border-transparent transition-colors duration-200 rounded-full px-4'
									)
								)}
								onClick={onClick}
							>
								<p className='mr-3 text-inherit'>Acessar aula ao vivo</p>
								<Icon
									name='arrow-right-4'
									width='18px'
									height='18px'
									className='text-[#150E1D]'
								/>
							</Button>
						)}
						{liveClass?.material && (
							<ActionButton
								isActive={isActive}
								onClick={() => window.open(liveClass.material)}
								iconName='download'
								classIsHappening={classIsHappening}
								buttonText={
									!classIsHappening ? 'Baixar material preparatório' : ''
								}
							/>
						)}
						{liveClass?.memhack && (
							<ActionButton
								isActive={isActive}
								onClick={() => window.open(liveClass.memhack)}
								iconName='memhack'
								classIsHappening={classIsHappening}
								buttonText={!classIsHappening ? 'Adicionar Memhack' : ''}
							/>
						)}
					</div>
				)}
			</div>
		</Link>
	)
}

const ActionButton = ({
	isActive,
	onClick,
	iconName,
	buttonText,
	classIsHappening,
}: {
	isActive?: boolean
	onClick: () => void
	iconName: string
	buttonText: string
	classIsHappening: boolean
}) => (
	<Button
		className={twMerge(
			classNames(
				'w-full max-w-max h-auto min-h-[40px] text-xs xs:text-sm bg-transparent opacity-50 hover:opacity-100 hover:bg-[#4E33AE]/50 border border-gray-400/10 border-solid hover:border-transparent transition-colors duration-200 rounded-full px-4',
				{
					'bg-[#4E33AE]/50 hover:bg-[#4E33AE] opacity-100 border-none':
						isActive,
				}
			)
		)}
		onClick={onClick}
	>
		<Icon name={iconName} width='18px' height='18px' />
		<p className={twMerge(classNames('ml-3 text-inherit', { 'ml-0': classIsHappening }))}>
			{buttonText}
		</p>
	</Button>
)
